import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import CountdownPill from "@/components/CountdownTimer";
import { ArrowRight } from "lucide-react";

// Up to 6 active vendor live banners. Expired ones disappear automatically —
// the backend filters by expires_at and we re-check every minute client-side too.
export default function LiveVendorBanners() {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/live/banners").then((r) => { if (!cancelled) setBanners(r.data || []); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const now = Date.now();
  const live = banners.filter((b) => !b.expires_at || new Date(b.expires_at).getTime() > now).slice(0, 6);
  if (live.length === 0) return null;

  const n = live.length;
  const gridClass = n === 1 ? "grid-cols-1" : n === 2 ? "grid-cols-1 sm:grid-cols-2" : n === 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-2 lg:grid-cols-3";

  return (
    <section id="live-vendors" data-testid="live-vendor-banners" className="mx-auto max-w-7xl px-4 lg:px-8 mt-4 sm:mt-6">
      <div className={`grid ${gridClass} gap-3 sm:gap-4`}>
        {live.map((b) => (
          <div key={b.id} data-testid={`live-banner-${b.vendor_id}`} className={`relative rounded-2xl overflow-hidden border border-gray-200 flex items-end ${n === 1 ? "min-h-[220px] sm:min-h-[280px]" : "min-h-[160px] sm:min-h-[200px]"}`}>
            {b.banner_image ? (
              <img src={b.banner_image} alt={b.vendor_business_name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#F48E2E 0%,#F6B266 50%,#FDEBD3 100%)" }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative w-full p-4 sm:p-5 flex flex-wrap items-end justify-between gap-3">
              <div className="text-white min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 font-bold flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" /></span>
                  Live showcase
                </div>
                <div className="font-display text-lg sm:text-xl truncate" data-testid={`live-banner-name-${b.vendor_id}`}>{b.vendor_business_name}</div>
                <div className="mt-1.5"><CountdownPill targetIso={b.expires_at} /></div>
              </div>
              <Link to={b.product_ids?.length ? `/product/${b.product_ids[0]}` : "/"} data-testid={`live-banner-shop-${b.vendor_id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-gray-900 px-4 h-9 text-xs font-semibold hover:bg-[#F48E2E] hover:text-white transition-colors">
                Shop Now <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
